import { useState, ChangeEvent, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';

import usePagination from '@hooks/usePagination';
import { manageProductTableHeader } from '@lib/data';
import { Query, Mutation, Product } from '@graphql/types';
import { SEE_PRODUCT_LIST } from '@graphql/queries';
import { DELETE_PRODUCT } from '@graphql/mutations';

import { FilterBox, Contents } from '@components/styled/StyledComponents';
import Headline from '@components/layout/Headline';
import StyledSelect from '@components/styled/StyledSelect';
import TableHeader from '@components/share/Table/TableHeader';
import { TableBody, EmptyTable } from '@components/share/Table/table.style';
import ManageProductTableRow from '@components/feature/Operation/ManageProduct/ManageProductTableRow';
import Pagination from '@components/layout/Pagination';
import Loader from '@components/share/Loader';
import AddProductModal from '@components/feature/Operation/ManageProduct/AddProductModal';

const ManageProduct = () => {
    const [addModal, setAddModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    // 페이지네이션 관련 상태
    const [take, setTake] = useState(10);
    const [preCursor, setPreCursor] = useState(0);
    const [nextCursor, setNextCursor] = useState(0);
    const [cursorOption, setCursorOption] = useState<boolean | null>(null);
    const [filter, setFilter] = useState({ order: 1 });

    const { data, loading } = useQuery<Pick<Query, 'seeProductList'>>(
        SEE_PRODUCT_LIST,
        {
            variables: {
                orderBy: +filter.order,
                take,
                preCursor,
                nextCursor,
                cursorOption,
                searchTerm
            },
            onError: (err) => {
                console.error(err);
            }
        }
    );
    const [deleteProduct] =
        useMutation<Pick<Mutation, 'deleteProduct'>>(DELETE_PRODUCT);

    const productList = data?.seeProductList?.productList ?? [];
    const totalLength = data?.seeProductList?.totalLength ?? 0;

    const onFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilter((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const onDelete = useCallback(async (id: number) => {
        if (!window.confirm('해당 상품을 삭제하시겠습니까?')) return;
        try {
            await deleteProduct({
                variables: {
                    prId: id
                },
                onCompleted: () => toast.success('삭제되었습니다.'),
                refetchQueries: [SEE_PRODUCT_LIST]
            });
        } catch (err) {
            console.error(err);
        }
    }, []);

    // 페이지네이션
    const {
        pageIndex,
        setPageIndex,
        pageGroupLength,
        prevPageHandler,
        nextPageHandler,
        prevPageGroupHandler,
        nextPageGroupHandler,
        resetPaginationHandler,
        disabled
    } = usePagination(
        take,
        totalLength,
        setPreCursor,
        setNextCursor,
        setCursorOption,
        productList,
        'prId'
    );

    return (
        <Contents>
            <Headline
                title="상품 관리"
                subTitle="상품 등록 및 기본 관리"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchCount={totalLength}
                buttonList={['상품등록']}
                buttonAction={[() => setAddModal(true)]}
                SEARCH
            />
            {/*  필터 영역 */}
            <FilterBox>
                <StyledSelect
                    title="정렬"
                    name="order"
                    value={filter.order}
                    onChange={onFilterChange}
                    options={[
                        { title: '내림차순', value: 1 },
                        { title: '오름차순', value: 2 }
                    ]}
                    DATA_VALUE
                />
            </FilterBox>
            <TableHeader header={manageProductTableHeader} />
            <TableBody $minHeight={500}>
                {loading && <Loader />}
                {totalLength === 0 ? (
                    <EmptyTable>상품이 없습니다.</EmptyTable>
                ) : (
                    productList
                        ?.slice(pageIndex, pageIndex + take)
                        .map((data, idx) => (
                            <ManageProductTableRow
                                key={`${data?.prId}-${idx}-product-list`}
                                header={manageProductTableHeader}
                                data={data as Product}
                                onDelete={onDelete}
                            />
                        ))
                )}
            </TableBody>
            {totalLength > 0 && (
                <Pagination
                    take={take}
                    setTake={setTake}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    pageGroupLength={pageGroupLength}
                    totalLength={totalLength}
                    prevPageHandler={prevPageHandler}
                    nextPageHandler={nextPageHandler}
                    prevPageGroupHandler={prevPageGroupHandler}
                    nextPageGroupHandler={nextPageGroupHandler}
                    resetPaginationHandler={resetPaginationHandler}
                    disabled={disabled}
                />
            )}
            {addModal && (
                <AddProductModal isOpen={addModal} setIsOpen={setAddModal} />
            )}
        </Contents>
    );
};

export default ManageProduct;
